import { defineStore } from 'pinia';
import { adminApi, type AdminIdentity, type AdminLoginInput } from '@/services/admin-api';
import { adminSession } from '@/services/http';

export const useAdminStore = defineStore('admin', {
  state: () => ({
    authenticated: Boolean(adminSession.token()),
    identity: (adminSession.username() ? {
      id: '',
      username: adminSession.username(),
      displayName: adminSession.displayName(),
      roles: [],
      permissions: [],
      menus: [],
    } : null) as AdminIdentity | null,
    dashboard: null as any,
    loading: false,
  }),
  actions: {
    async login(input: AdminLoginInput, rememberUsername: boolean) {
      this.loading = true;
      try {
        const result = await adminApi.login(input);
        adminSession.save(result.accessToken, result.admin);
        adminSession.rememberUsername(result.admin.username, rememberUsername);
        this.identity = result.admin;
        this.authenticated = true;
      } finally {
        this.loading = false;
      }
    },
    logout() {
      const finish = () => {
        adminSession.clear();
        this.identity = null;
        this.authenticated = false;
      };
      return adminApi.logout().catch(() => undefined).finally(finish);
    },
    async loadIdentity(force = false) {
      if (!adminSession.token()) return null;
      if (!force && this.identity?.menus?.length) return this.identity;
      try {
        this.identity = await adminApi.me();
        this.authenticated = true;
        return this.identity;
      } catch (error) {
        adminSession.clear();
        this.identity = null;
        this.authenticated = false;
        throw error;
      }
    },
    can(permission?: string) {
      if (!permission) return true;
      return Boolean(this.identity?.permissions.includes('*') || this.identity?.permissions.includes(permission));
    },
    async loadDashboard() {
      this.loading = true;
      try {
        this.dashboard = await adminApi.dashboard();
      } finally {
        this.loading = false;
      }
    },
  },
});
